import {
  AccessTime,
  Business,
  CheckCircle,
  MedicationLiquid,
  Stop,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";

import { DocumentDateBadge } from "@/components/common/prescriptionStack/DocumentDateBadge";
import { extractTimeFromResource } from "@/components/common/prescriptionStack/dateBadgeUtils";
import { ExpandableStatusChip } from "@/components/common/prescriptionStack/ExpandableStatusChip";
import { PatientPrescriptionCardActions } from "@/components/common/prescriptionStack/PatientPrescriptionCardActions";
import {
  extractOrganizationName,
  extractBinaryUrl,
  findDocumentReferenceForMedication,
  formatMedicationPosology,
  isDocumentSigned,
  processDocumentBaseData,
} from "@/utils/fhirDocumentUtils";

import { HealthMedicineDetails } from "../HealthMedicineDetails";
import {
  getMedicationCategoryLabel,
  getMedicationScheduleLabel,
} from "./medicationStackUtils";

export interface MedicationStackItem {
  type: "medication" | "examSolicitation";
  name?: string;
  date?: string;
  fullResource?: any;
  result?: any;
}

const CARD_BORDER = {
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
};

function getStatusChip(status?: string) {
  switch (status) {
    case "active":
      return { label: "Ativo", color: "success" as const, icon: <CheckCircle /> };
    case "stopped":
      return { label: "Interrompido", color: "error" as const, icon: <Stop /> };
    case "completed":
      return { label: "Concluído", color: "success" as const, icon: <CheckCircle /> };
    default:
      return { label: "Pendente", color: "warning" as const, icon: <AccessTime /> };
  }
}

function MedicationPrescriptionBack({
  organization,
  posologyLine,
  signed,
}: {
  organization?: string | null;
  posologyLine?: { name: string; posology: string };
  signed: boolean;
}) {
  return (
    <Box
      sx={{
        ...CARD_BORDER,
        flex: 1,
        minWidth: 0,
        p: 1.25,
        bgcolor: "grey.50",
        display: "flex",
        flexDirection: "column",
        borderLeft: 0,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      }}
    >
      <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "text.secondary" }}>
            RECEITA
          </Typography>
          <ExpandableStatusChip signed={signed} />
        </Stack>

        <Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
            <Business sx={{ fontSize: 14, color: "primary.main" }} />
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "text.secondary" }}>
              ORGANIZAÇÃO
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: "0.78rem", lineHeight: 1.35, pl: 2.25, wordBreak: "break-word" }}>
            {organization || "Não informada"}
          </Typography>
        </Box>

        {posologyLine && (
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
              <MedicationLiquid sx={{ fontSize: 14, color: "primary.main" }} />
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "text.secondary" }}>
                POSOLOGIA
              </Typography>
            </Stack>
            <Box sx={{ pl: 1.5, borderLeft: "2px solid", borderColor: "primary.light" }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, lineHeight: 1.3, wordBreak: "break-word" }}>
                {posologyLine.name}
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.4, mt: 0.15 }}>
                {posologyLine.posology}
              </Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function MedicationMobileCard({
  item,
  setRefetch,
  prescriptionDocuments,
  handleOpen,
  setBinaryUrl,
}: {
  item: MedicationStackItem;
  setRefetch?: (prev: any) => void;
  prescriptionDocuments?: any[];
  handleOpen?: () => void;
  setBinaryUrl?: (url: string) => void;
}) {
  const theme = useTheme();
  const [flipped, setFlipped] = useState(false);
  const status = getStatusChip(item.fullResource?.status);
  const schedule = getMedicationScheduleLabel(item.fullResource);
  const category = getMedicationCategoryLabel(item.fullResource);
  const time = extractTimeFromResource(item.fullResource);

  const badgeColor =
    item.fullResource?.status === "active"
      ? theme.palette.primary.main
      : item.fullResource?.status === "stopped"
        ? theme.palette.error.main
        : theme.palette.warning.dark;

  const linkedDocument = useMemo(
    () =>
      findDocumentReferenceForMedication(
        item.fullResource?.id,
        prescriptionDocuments ?? [],
      ),
    [item.fullResource?.id, prescriptionDocuments],
  );

  const prescriptionMeta = useMemo(() => {
    if (!linkedDocument) return null;
    const resource = linkedDocument?.resource ?? linkedDocument;
    const signed = isDocumentSigned(resource);
    const baseData = processDocumentBaseData(linkedDocument, signed);
    const posologyLine = formatMedicationPosology(item.fullResource);
    return {
      signed,
      organization: extractOrganizationName(resource),
      binaryUrl: baseData.binaryUrl || extractBinaryUrl(resource),
      posologyLine,
    };
  }, [linkedDocument, item.fullResource]);

  const hasPrescription = Boolean(prescriptionMeta);
  const toggleFlip = () => {
    if (hasPrescription) setFlipped((prev) => !prev);
  };

  const cardContent = (
    <Box sx={{ flex: 1, minWidth: 0, p: 1.25 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={0.75}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.85rem",
            lineHeight: 1.3,
            wordBreak: "break-word",
            flex: 1,
          }}
        >
          {item.name?.trim() || "Medicamento"}
        </Typography>

        <Chip
          icon={status.icon}
          label={status.label}
          size="small"
          color={status.color}
          variant="outlined"
          sx={{
            height: 22,
            fontSize: "0.62rem",
            fontWeight: 600,
            flexShrink: 0,
            "& .MuiChip-icon": { fontSize: 13 },
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      </Stack>

      {(schedule || category) && (
        <Typography
          sx={{
            mt: 0.35,
            fontSize: "0.7rem",
            color: "text.secondary",
            lineHeight: 1.35,
          }}
        >
          {[schedule, category !== "MEDICAMENTO" ? category : null]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      )}

      {hasPrescription && (
        <Box sx={{ mt: 0.75 }} onClick={(e) => e.stopPropagation()}>
          <PatientPrescriptionCardActions
            compact
            row={{
              binaryUrl: prescriptionMeta?.binaryUrl,
              description: prescriptionMeta?.posologyLine?.name,
            }}
            handleOpen={handleOpen}
            setBinaryUrl={setBinaryUrl}
          />
        </Box>
      )}

      <Box sx={{ mt: 0.75 }} onClick={(e) => e.stopPropagation()}>
        <HealthMedicineDetails
          variant="stack"
          type={item.type}
          name={item.name}
          date={item.date}
          fullData={item.fullResource}
          result={item.result}
          setRefetch={setRefetch}
        />
      </Box>
    </Box>
  );

  const cardRow = (
    <Box sx={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "stretch" }}>
      {item.date && (
        <DocumentDateBadge
          date={item.date}
          time={time}
          accentColor={badgeColor}
        />
      )}
      {cardContent}
    </Box>
  );

  if (!hasPrescription) {
    return (
      <Box sx={{ ...CARD_BORDER, display: "flex", flexDirection: "row", overflow: "hidden" }}>
        {cardRow}
      </Box>
    );
  }

  const faceShellSx = {
    display: "flex",
    width: "100%",
    backfaceVisibility: "hidden" as const,
    WebkitBackfaceVisibility: "hidden" as const,
  };

  return (
    <Box sx={{ perspective: "1000px", width: "100%" }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          transformStyle: "preserve-3d",
          transition: "transform 0.5s ease-in-out",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        <Box
          onClick={toggleFlip}
          sx={{
            ...faceShellSx,
            transform: "rotateY(0deg)",
            pointerEvents: flipped ? "none" : "auto",
            cursor: "pointer",
            ...CARD_BORDER,
            overflow: "hidden",
          }}
        >
          {cardRow}
        </Box>

        <Box
          onClick={toggleFlip}
          sx={{
            ...faceShellSx,
            position: "absolute",
            inset: 0,
            transform: "rotateY(180deg)",
            pointerEvents: flipped ? "auto" : "none",
            cursor: "pointer",
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", width: "100%", height: "100%" }}>
            {item.date && (
              <DocumentDateBadge
                date={item.date}
                time={time}
                accentColor={badgeColor}
              />
            )}
            <MedicationPrescriptionBack
              organization={prescriptionMeta?.organization}
              posologyLine={prescriptionMeta?.posologyLine}
              signed={prescriptionMeta?.signed ?? false}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface MedicationMobileListProps {
  items: MedicationStackItem[];
  setRefetch?: (prev: any) => void;
  isRefetching?: boolean;
  prescriptionDocuments?: any[];
  handleOpen?: () => void;
  setBinaryUrl?: (url: string) => void;
  /** Quando informado, exibe os cards em grid (ex.: 3 colunas no web). */
  columns?: number;
}

export function MedicationMobileList({
  items,
  setRefetch,
  isRefetching,
  prescriptionDocuments,
  handleOpen,
  setBinaryUrl,
  columns,
}: MedicationMobileListProps) {
  const useGrid = typeof columns === "number" && columns > 1;

  return (
    <Box
      sx={{
        width: "100%",
        opacity: isRefetching ? 0.7 : 1,
        transition: "opacity 0.2s ease",
        ...(useGrid
          ? {
              display: "grid",
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              gap: 2,
              alignItems: "stretch",
            }
          : {
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }),
      }}
    >
      {items.map((item, index) => (
        <MedicationMobileCard
          key={item.fullResource?.id ?? `${item.name}-${index}`}
          item={item}
          setRefetch={setRefetch}
          prescriptionDocuments={prescriptionDocuments}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
        />
      ))}
    </Box>
  );
}

/** @deprecated Use MedicationMobileList */
export function MedicationStackCarousel(props: MedicationMobileListProps) {
  return <MedicationMobileList {...props} />;
}
